import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { uuidv7 as uuid } from "uuidv7";
import { TaskType } from "src/types";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ReactComponent as FilterIcon } from "../../../../images/filter.svg";
import {
  PaneTopbar,
  PaneTopbarLeft,
  PaneTopbarToolbar,
} from "../../../components/Pane/PaneTopbar/PaneTopbar";
import { setSelectedFilters } from "../../../../redux";
import { Button } from "../../../components/Button/Button";
import { actionPredefinedFilters } from "./ActionPredefinedFilters";
import SingleFilter from "../../ProjectsList/partials/ProjectsTable/tableComponents/ProjectsTableFilters/components/SingleFilter/SingleFilter";
import { valueExists } from "src/app/methods/valueExists";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import { capitalizeFirstLetter } from "src/utils/methods";

import "./TasksTableFilters.scss";

export default function TasksTableFilters({
  setFiltersVisible,
  taskType,
  data,
}) {
  const [renderKey, setRenderKey] = useState(uuid());
  const {
    taskReducer: { tasksColumns },
    taskFiltersReducer: { selectedFilters },
  } = useSelector((state) => state);

  const dispatch = useDispatch();

  const sendFilter = (uuid, value) => {
    const doesValueExist = valueExists(value);

    let newFilters = { ...selectedFilters };

    if (doesValueExist) {
      newFilters = { ...selectedFilters, [uuid]: { value } };
    } else {
      delete newFilters[uuid];
    }

    dispatch(setSelectedFilters(newFilters));
  };

  const clearFilters = () => {
    dispatch(setSelectedFilters({}));
    setRenderKey(uuid());
  };

  const visibleColumns = useMemo(() => {
    return tasksColumns
      .filter((c) => c.metaFieldVisible && !c.metaFieldIsSystem)
      .map((c) => ({
        data: c.data,
        wsGlobalTaskMetaFieldUuid: c.metaFieldGlobalTaskMetaFieldUuid,
        uuid: c.metaFieldId,
        idRequired: c.metaFieldIsRequired,
        isSystem: c.metaFieldIsSystem,
        key: c.metaFieldKey,
        name: c.metaFieldName,
        rank: c.metaFieldRank,
        shared: c.metaFieldShared,
        isSharedEditable: c.metaFieldSharedEditable,
        fieldValueSource: c.metaFieldSource,
        taskType: c.metaFieldTaskType,
        type: c.metaFieldType,
        valueSource: c.metaFieldValueSource,
        isVisible: c.metaFieldVisible,
      }));
  }, [tasksColumns]);

  const titleUuid = useMemo(() => {
    return data[0]?.metadata.find((c) => c.key === "title")?.uuid;
  }, [data]);

  return (
    <>
      <PaneTopbar>
        <PaneTopbarLeft>
          <FilterIcon className="filters-header__icon" />
          <span className="filters-header__title">
            <IDHFormattedMessage id="ws_filter" defaultMessage="Filter" />
          </span>
        </PaneTopbarLeft>
        <PaneTopbarToolbar hidePane={() => setFiltersVisible(false)} />
      </PaneTopbar>
      <div className="pane__container filters campaign-items-filters">
        <SingleFilter
          col={{
            name: `${capitalizeFirstLetter(taskType)} name`,
            type: MetaFieldType.Text,
            uuid: titleUuid,
          }}
          sendFilter={sendFilter}
          defaultValue={selectedFilters?.taskNameOrUuid?.value}
          key={`${renderKey}-${titleUuid}`}
          filters={selectedFilters}
        />
        {(taskType === TaskType.Action || taskType === TaskType.Payment) && (
          <>
            {actionPredefinedFilters.map((col) => (
              <SingleFilter
                col={col}
                sendFilter={sendFilter}
                defaultValue={selectedFilters[col.uuid]?.value}
                filters={selectedFilters}
                key={`${renderKey}-${col.uuid}`}
              />
            ))}
          </>
        )}

        {visibleColumns.map((col) => (
          <SingleFilter
            col={col}
            sendFilter={sendFilter}
            defaultValue={selectedFilters[col.uuid]?.value}
            filters={selectedFilters}
            key={`${renderKey}-${col.uuid}`}
          />
        ))}

        {taskType !== TaskType.Action &&
          !taskType !== TaskType.Payment &&
          visibleColumns.length === 0 && (
            <div className="filter-box-empty">
              <IDHFormattedMessage
                id="ws_no_data_to_display"
                defaultMessage="No data to display"
              />
            </div>
          )}
      </div>
      <Button
        variant="light"
        size="medium"
        className="filters-button"
        onClick={clearFilters}
        data-qa="clear-filters-button"
      >
        <IDHFormattedMessage
          id="ws_clear_filters"
          defaultMessage="Clear filters"
        />
      </Button>
    </>
  );
}
